/* AutomationsPage.jsx — Intervention Command Desk.
 *
 * Operational governance layer for ChainFlow OS. Six sections:
 *  1. Executive Automation Hero
 *  2. Pending Approvals Queue (grouped by rule)
 *  3. Rule Activity & Health
 *  4. Recent Automation Timeline (reuses InterventionAuditPanel language)
 *  5. Rule Catalogue (enable/disable/edit)
 *  6. Operational Governance Footer
 *
 * Doctrine:
 *  • Inherits the Director Centre visual identity — no redesign.
 *  • Bulk approve/reject still emit one run + one DealActivity per run.
 *  • Calm tone everywhere — no marketing, no AI hype.
 *  • Smart polling (15s when tab visible) — swap to WS later via hook.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, Sparkles, ChevronDown, ChevronUp, Activity,
  AlertOctagon, GitBranch, Send, FileText, CheckCircle2, XCircle,
  ArrowUpRight, Eye, Power, Pencil, X, Save, ClipboardList,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useLiveAutomations } from "../hooks/useLiveAutomations";
import { PremiumSegmented } from "../components/brand/PremiumSegmented";
import { PremiumInput } from "../components/brand/PremiumInput";
import { ATTENTION_TONE, attentionToneFor, fmtGBP, fmtRelativeTime } from "../components/director/attentionTone";
import { PlaybookCard } from "../components/playbooks/PlaybookCard";
import { DirectorShell } from "../components/director/DirectorShell";


/* ─── helpers ──────────────────────────────────────────────────────── */
const TRIGGER_ICON = {
  solicitor_silence_days:   Send,
  aml_overdue:              FileText,
  mortgage_offer_expiring:  AlertOctagon,
  mgmt_pack_stalled:        FileText,
  completion_drift:         GitBranch,
  participant_silent:       Send,
  prolonged_inactivity:     Activity,
  chain_propagation_alert:  GitBranch,
};

const severityTone = (sev) =>
  attentionToneFor(
    sev === "critical" ? "Critical" :
    sev === "elevated" ? "Elevated" :
    sev === "monitoring" ? "Monitoring" : "Stable"
  );

/* ─── 1. Hero ─────────────────────────────────────────────────────── */
const HeroKpi = ({ label, value, accent }) => (
  <div className="rounded-xl px-4 py-3.5"
       style={{ background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.10)" }}
       data-testid={`auto-hero-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-[9.5px] uppercase tracking-[0.16em] font-bold"
       style={{ color: "rgba(255,255,255,0.55)" }}>{label}</p>
    <p className="text-[20px] font-semibold tabular-nums tracking-tight mt-1"
       style={{ color: accent || "#fff" }}>{value}</p>
  </div>
);

const AutomationHero = ({ summary }) => {
  const h = summary?.hero || {};
  return (
    <section
      className="relative overflow-hidden rounded-2xl sm:rounded-3xl mb-7 px-5 sm:px-8 py-6 sm:py-7"
      data-testid="automation-hero"
      style={{
        background: "linear-gradient(135deg, #0B1024 0%, #161A33 50%, #1F1A3E 100%)",
        boxShadow:
          "0 26px 60px -36px rgba(15,23,42,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)",
      }}
    >
      <span aria-hidden
        className="absolute -top-16 right-0 w-[260px] h-[260px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.22), transparent 70%)" }} />
      <div className="relative">
        <div className="flex items-start gap-2 mb-2">
          <Sparkles size={12} strokeWidth={2.3} style={{ color: "#A78BFA" }} />
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold"
             style={{ color: "#A78BFA" }}>Intervention Command Desk</p>
        </div>
        <h1 className="text-[21px] sm:text-[28px] font-semibold tracking-tight text-white leading-snug"
            data-testid="automation-hero-headline">
          {summary?.narrative || "Awaiting first operational interventions."}
        </h1>
        <p className="mt-2 text-[12px] sm:text-[13px] leading-relaxed max-w-2xl"
           style={{ color: "rgba(255,255,255,0.65)" }}>
          Approval-gated operational automation · immutable audit lineage · tenancy-safe execution.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 sm:gap-3 mt-5">
          <HeroKpi label="Pending"          value={h.pending_total ?? "—"}
                   accent={h.pending_total ? "#FDE68A" : "#fff"} />
          <HeroKpi label="Fired today"      value={h.fired_today ?? "—"} />
          <HeroKpi label="Approval rate"    value={h.approval_rate_pct != null ? `${h.approval_rate_pct}%` : "—"}
                   accent={h.approval_rate_pct >= 75 ? "#A7F3D0" : "#fff"} />
          <HeroKpi label="Critical pending" value={h.critical_pending ?? "—"}
                   accent={h.critical_pending ? "#FCA5A5" : "#fff"} />
          <HeroKpi label="Avg response"
                   value={h.avg_response_minutes != null ? `${h.avg_response_minutes}m` : "—"} />
        </div>
      </div>
    </section>
  );
};

/* ─── 2. Pending Approvals Queue ──────────────────────────────────── */
const RunRow = ({ run, onApprove, onReject, onReview }) => {
  const snap = run.evaluation_snapshot || {};
  const tone = severityTone(snap.severity);
  const [playbookOpen, setPlaybookOpen] = useState(false);
  const [playbook, setPlaybook] = useState(null);
  const [pbLoading, setPbLoading] = useState(false);
  const [pbError, setPbError] = useState(null);
  const navigate = useNavigate();

  const fetchPlaybook = async () => {
    setPbLoading(true);
    setPbError(null);
    try {
      const res = await api.get(`/api/playbooks/runs/${run.id}`);
      setPlaybook(res.data?.playbook || null);
    } catch (e) {
      if (e?.response?.status === 404) {
        setPbError("No operational playbook is registered for this trigger.");
      } else {
        setPbError(e?.response?.data?.detail || "Could not load the playbook.");
      }
    } finally {
      setPbLoading(false);
    }
  };

  const togglePlaybook = () => {
    if (!playbookOpen && !playbook) fetchPlaybook();
    setPlaybookOpen((v) => !v);
  };

  const completeStep = async (stepId) => {
    try {
      const res = await api.post(
        `/api/playbooks/runs/${run.id}/steps/${stepId}/complete`, {},
      );
      if (res.data?.already_complete) {
        toast("Step already recorded as complete.");
      } else {
        toast.success("Step recorded · audit trail updated.");
      }
      await fetchPlaybook();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record step.");
    }
  };

  return (
    <div className="p-3 sm:p-3.5 rounded-lg"
         style={{ border: "1px solid var(--gp-card-border)",
                  background: "var(--gp-card-bg)" }}
         data-testid={`auto-run-${run.id}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-8 h-8 rounded-lg inline-flex items-center justify-center"
             style={{ background: tone.bg, color: tone.fg,
                      border: `1px solid ${tone.border}` }}>
          <AlertOctagon size={13} strokeWidth={2.3} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] font-semibold leading-tight"
             style={{ color: "var(--gp-text)" }}>
            {run.action_payload?.label || "Operational intervention"}
          </p>
          <p className="text-[11px] mt-0.5"
             style={{ color: "var(--gp-text-muted)" }}>
            {snap.fired_reason}
          </p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className="text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ background: tone.bg, color: tone.fg }}>
              {tone.short}
            </span>
            <span className="text-[10.5px] tabular-nums"
                  style={{ color: "var(--gp-text-subtle)" }}>
              Waiting · {fmtRelativeTime(run.fired_at)}
            </span>
            <button
              type="button"
              onClick={togglePlaybook}
              className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded"
              style={{ background: "rgba(99,102,241,0.08)",
                       color: "#4338CA",
                       border: "1px solid rgba(99,102,241,0.18)" }}
              data-testid={`auto-playbook-toggle-${run.id}`}
            >
              <ClipboardList size={10} strokeWidth={2.3} />
              {playbookOpen ? "Hide playbook" : "Open playbook"}
            </button>
          </div>
        </div>
        <div className="shrink-0 flex flex-col gap-1.5 items-end">
          <button
            type="button"
            onClick={() => onApprove(run.id)}
            className="text-[11px] font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors"
            style={{ background: "rgba(16,185,129,0.10)",
                     color: "#047857",
                     border: "1px solid rgba(16,185,129,0.22)" }}
            data-testid={`auto-approve-${run.id}`}>
            <CheckCircle2 size={11} /> Approve
          </button>
          <button
            type="button"
            onClick={() => onReject(run.id)}
            className="text-[11px] font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors"
            style={{ background: "rgba(148,163,184,0.08)",
                     color: "#475569",
                     border: "1px solid rgba(148,163,184,0.20)" }}
            data-testid={`auto-reject-${run.id}`}>
            <XCircle size={11} /> Reject
          </button>
          <button
            type="button"
            onClick={() => onReview(run.deal_id)}
            className="text-[10.5px] font-semibold inline-flex items-center gap-1 mt-0.5"
            style={{ color: "var(--gp-violet-deep)" }}
            data-testid={`auto-review-${run.id}`}>
            Review deal <ArrowUpRight size={10} />
          </button>
        </div>
      </div>
      {playbookOpen ? (
        <div className="mt-3" data-testid={`auto-playbook-${run.id}`}>
          {pbLoading ? (
            <p className="text-[11.5px] text-slate-500 px-3 py-2">
              Loading operational playbook…
            </p>
          ) : pbError ? (
            <p className="text-[11.5px] text-slate-500 px-3 py-2">{pbError}</p>
          ) : playbook ? (
            <PlaybookCard
              playbook={playbook}
              defaultOpen
              onCompleteStep={completeStep}
              onReviewDeal={(did) => navigate(`/deal-rooms/${did}`)}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const PendingGroupCard = ({ group, rules, onApproveOne, onRejectOne,
                            onBulkApprove, onBulkReject, onReviewDeal }) => {
  const [open, setOpen] = useState(true);
  const rule = rules.find((r) => r.id === group.rule_id);
  const Icon = TRIGGER_ICON[rule?.trigger_kind] || Activity;
  const ruleName = rule?.name || group.rule_id;
  const oldest = group.runs.reduce(
    (acc, r) => (acc && acc.fired_at < r.fired_at ? acc : r),
    null,
  );
  const tone = severityTone(group.runs[0]?.evaluation_snapshot?.severity);
  return (
    <div className="gp-card overflow-hidden"
         style={{ borderLeft: `3px solid ${tone.dot}` }}
         data-testid={`auto-group-${group.rule_id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-4 flex items-start gap-3"
      >
        <div className="shrink-0 w-9 h-9 rounded-lg inline-flex items-center justify-center"
             style={{ background: tone.bg, color: tone.fg,
                      border: `1px solid ${tone.border}` }}>
          <Icon size={14} strokeWidth={2.3} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-[13.5px] font-semibold text-slate-900 leading-tight">
              {ruleName}
            </h3>
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ background: tone.bg, color: tone.fg }}>
              {group.runs.length} pending
            </span>
          </div>
          <p className="text-[11px] mt-1 leading-snug"
             style={{ color: "var(--gp-text-muted)" }}>
            Oldest waiting {fmtRelativeTime(oldest?.fired_at)} · {rule?.description || ""}
          </p>
        </div>
        {open ? <ChevronUp size={14} className="shrink-0 mt-2 text-slate-400" />
              : <ChevronDown size={14} className="shrink-0 mt-2 text-slate-400" />}
      </button>
      {open ? (
        <div className="px-4 pb-4 space-y-2"
              style={{ borderTop: "1px solid var(--gp-card-border)" }}>
          <div className="flex items-center justify-end gap-2 pt-3 pb-1 flex-wrap">
            <button type="button"
              onClick={() => onBulkApprove(group.rule_id)}
              className="text-[10.5px] font-semibold px-2.5 py-1 rounded-md"
              style={{ background: "rgba(16,185,129,0.10)", color: "#047857",
                       border: "1px solid rgba(16,185,129,0.22)" }}
              data-testid={`auto-bulk-approve-${group.rule_id}`}>
              Approve all from this rule
            </button>
            <button type="button"
              onClick={() => onBulkReject(group.rule_id)}
              className="text-[10.5px] font-semibold px-2.5 py-1 rounded-md"
              style={{ background: "rgba(148,163,184,0.08)", color: "#475569",
                       border: "1px solid rgba(148,163,184,0.20)" }}
              data-testid={`auto-bulk-reject-${group.rule_id}`}>
              Reject all
            </button>
          </div>
          {group.runs.map((r) => (
            <RunRow key={r.id} run={r}
                    onApprove={onApproveOne}
                    onReject={onRejectOne}
                    onReview={onReviewDeal} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const PendingApprovalsQueue = ({ runs, rules, refetch }) => {
  const navigate = useNavigate();
  const groups = useMemo(() => {
    const byRule = {};
    for (const r of runs) {
      (byRule[r.rule_id] = byRule[r.rule_id] || { rule_id: r.rule_id, runs: [] }).runs.push(r);
    }
    return Object.values(byRule).sort(
      (a, b) => b.runs.length - a.runs.length,
    );
  }, [runs]);

  const approveOne = async (run_id) => {
    try {
      await api.post(`/api/automations/runs/${run_id}/approve`, { notes: null });
      toast.success("Automation approved");
      refetch();
    } catch (e) {
      toast.error("Could not approve · " + (e?.response?.data?.detail || "please retry"));
    }
  };
  const rejectOne = async (run_id) => {
    try {
      await api.post(`/api/automations/runs/${run_id}/reject`, { notes: null });
      toast("Automation rejected");
      refetch();
    } catch (e) {
      toast.error("Could not reject · " + (e?.response?.data?.detail || "please retry"));
    }
  };
  const bulkApprove = async (rule_id) => {
    try {
      const res = await api.post("/api/automations/runs/bulk-approve", { rule_id });
      toast.success(`Approved ${res.data.count} pending runs`);
      refetch();
    } catch (e) {
      toast.error("Bulk approve failed");
    }
  };
  const bulkReject = async (rule_id) => {
    try {
      const res = await api.post("/api/automations/runs/bulk-reject", { rule_id });
      toast(`Rejected ${res.data.count} pending runs`);
      refetch();
    } catch (e) {
      toast.error("Bulk reject failed");
    }
  };
  const reviewDeal = (deal_id) => deal_id && navigate(`/deal-rooms/${deal_id}`);

  if (groups.length === 0) {
    return (
      <section className="mb-7" data-testid="auto-empty-pending">
        <div className="gp-card p-7 text-center">
          <ShieldCheck size={20} className="mx-auto mb-2 text-emerald-600" />
          <p className="text-[13px] font-semibold text-slate-900">
            No operational interventions awaiting review.
          </p>
          <p className="text-[11.5px] mt-1" style={{ color: "var(--gp-text-muted)" }}>
            The automation queue is clear. The page updates every 15 seconds.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-7" data-testid="auto-pending-queue">
      <div className="flex items-center gap-2 mb-3">
        <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--gp-violet)" }} />
        <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">
          Pending Approvals
        </p>
      </div>
      <div className="space-y-3">
        {groups.map((g) => (
          <PendingGroupCard key={g.rule_id} group={g} rules={rules}
                            onApproveOne={approveOne} onRejectOne={rejectOne}
                            onBulkApprove={bulkApprove} onBulkReject={bulkReject}
                            onReviewDeal={reviewDeal} />
        ))}
      </div>
    </section>
  );
};

/* ─── 3. Rule Activity & Health ────────────────────────────────────── */
const RuleHealthRow = ({ row }) => {
  const Icon = TRIGGER_ICON[row.trigger_kind] || Activity;
  const rate = row.approval_rate_pct || 0;
  const rateColour = rate >= 75 ? "#10B981" : rate >= 50 ? "#A78BFA" : "#F59E0B";
  return (
    <div className="gp-card p-3.5 sm:p-4" data-testid={`auto-health-${row.rule_id}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-8 h-8 rounded-lg inline-flex items-center justify-center"
             style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA",
                      border: "1px solid rgba(99,102,241,0.22)" }}>
          <Icon size={13} strokeWidth={2.3} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold leading-tight truncate"
             style={{ color: "var(--gp-text)" }}>{row.rule_name}</p>
          <p className="text-[10.5px] mt-0.5"
             style={{ color: "var(--gp-text-muted)" }}>
            {row.trigger_label} → {row.action_label}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2.5">
            <div>
              <p className="text-[9.5px] uppercase tracking-[0.14em] font-bold"
                 style={{ color: "var(--gp-text-muted)" }}>Fired</p>
              <p className="text-[12.5px] font-semibold tabular-nums"
                 style={{ color: "var(--gp-text)" }}>{row.fired_total}</p>
            </div>
            <div>
              <p className="text-[9.5px] uppercase tracking-[0.14em] font-bold"
                 style={{ color: "var(--gp-text-muted)" }}>Approval %</p>
              <p className="text-[12.5px] font-semibold tabular-nums"
                 style={{ color: rateColour }}>{rate}%</p>
            </div>
            <div>
              <p className="text-[9.5px] uppercase tracking-[0.14em] font-bold"
                 style={{ color: "var(--gp-text-muted)" }}>Pending</p>
              <p className="text-[12.5px] font-semibold tabular-nums"
                 style={{ color: row.pending ? "#92400E" : "var(--gp-text)" }}>
                {row.pending}
              </p>
            </div>
            <div>
              <p className="text-[9.5px] uppercase tracking-[0.14em] font-bold"
                 style={{ color: "var(--gp-text-muted)" }}>Last fired</p>
              <p className="text-[11.5px] tabular-nums"
                 style={{ color: "var(--gp-text-muted)" }}>
                {row.last_run_at ? fmtRelativeTime(row.last_run_at) : "—"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RuleActivityHealth = ({ summary }) => {
  const health = summary?.rule_health || [];
  if (health.length === 0) return null;
  return (
    <section className="mb-7" data-testid="auto-health">
      <div className="flex items-center gap-2 mb-3">
        <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--gp-violet)" }} />
        <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">
          Rule Activity &amp; Health
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {health.map((r) => <RuleHealthRow key={r.rule_id} row={r} />)}
      </div>
    </section>
  );
};

/* ─── 4. Recent Automation Timeline ─────────────────────────────────── */
const STATUS_LABEL = {
  pending_approval: "Awaiting approval",
  executed:         "Approved",
  cancelled:        "Resolved",
  skipped:          "Skipped",
  failed:           "Failed",
};

const RecentAutomationTimeline = ({ recent, rules }) => {
  const navigate = useNavigate();
  const rulesById = useMemo(
    () => Object.fromEntries(rules.map((r) => [r.id, r])),
    [rules],
  );
  if (!recent || recent.length === 0) return null;
  return (
    <section className="gp-card p-4 sm:p-6 mb-7" data-testid="auto-recent-timeline">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-7 h-7 rounded-md inline-flex items-center justify-center"
             style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}>
          <Activity size={13} strokeWidth={2.3} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">
            Recent Automation Timeline
          </p>
          <h2 className="text-[12.5px] sm:text-[13.5px] font-semibold text-slate-900 leading-snug">
            Immutable operational lineage · last 30 runs
          </h2>
        </div>
      </div>
      <div className="space-y-2">
        {recent.map((r) => {
          const rule = rulesById[r.rule_id];
          const snap = r.evaluation_snapshot || {};
          const tone = severityTone(snap.severity);
          const Icon = TRIGGER_ICON[rule?.trigger_kind] || Activity;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => r.deal_id && navigate(`/deal-rooms/${r.deal_id}`)}
              className="w-full text-left p-3 rounded-lg transition-all hover:bg-slate-50/50"
              style={{ border: "1px solid var(--gp-card-border)" }}
              data-testid={`auto-timeline-${r.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-8 h-8 rounded-lg inline-flex items-center justify-center"
                     style={{ background: tone.bg, color: tone.fg,
                              border: `1px solid ${tone.border}` }}>
                  <Icon size={13} strokeWidth={2.3} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] font-semibold"
                     style={{ color: "var(--gp-text)" }}>
                    {rule?.name || "Automation"} · {STATUS_LABEL[r.status] || r.status}
                  </p>
                  <p className="text-[11px] mt-0.5 leading-snug"
                     style={{ color: "var(--gp-text-muted)" }}>
                    {snap.fired_reason}
                  </p>
                </div>
                <p className="shrink-0 text-[10.5px] tabular-nums whitespace-nowrap"
                   style={{ color: "var(--gp-text-subtle)" }}>
                  {fmtRelativeTime(r.fired_at)}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

/* ─── 5. Rule Catalogue ─────────────────────────────────────────────── */
const RuleCatalogueCard = ({ rule, refetch }) => {
  const [editing, setEditing] = useState(false);
  const [threshold, setThreshold] = useState(
    rule.trigger_config?.threshold_days
      ?? rule.trigger_config?.window_days
      ?? rule.trigger_config?.min_propagation_count
      ?? "",
  );
  const [approval, setApproval] = useState(rule.approval_required);
  const [busy, setBusy] = useState(false);

  const toggleEnabled = async () => {
    setBusy(true);
    try {
      await api.patch(`/api/automations/rules/${rule.id}`, { enabled: !rule.enabled });
      toast.success(rule.enabled ? "Rule disabled" : "Rule enabled");
      refetch();
    } catch (e) {
      toast.error("Could not toggle rule");
    } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const cfgKey = rule.trigger_config?.threshold_days != null
        ? "threshold_days"
        : rule.trigger_config?.window_days != null
          ? "window_days"
          : "min_propagation_count";
      const payload = {
        approval_required: approval,
        trigger_config: { [cfgKey]: parseInt(threshold || "0", 10) },
      };
      await api.patch(`/api/automations/rules/${rule.id}`, payload);
      toast.success("Rule updated");
      setEditing(false);
      refetch();
    } catch (e) {
      toast.error("Could not save · " + (e?.response?.data?.detail || ""));
    } finally { setBusy(false); }
  };
  const Icon = TRIGGER_ICON[rule.trigger_kind] || Activity;
  return (
    <div className="gp-card p-4" data-testid={`auto-rule-${rule.id}`}>
      <div className="flex items-start gap-3 mb-2">
        <div className="shrink-0 w-8 h-8 rounded-lg inline-flex items-center justify-center"
             style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA",
                      border: "1px solid rgba(99,102,241,0.22)" }}>
          <Icon size={13} strokeWidth={2.3} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold leading-tight"
             style={{ color: "var(--gp-text)" }}>{rule.name}</p>
          <p className="text-[10.5px] mt-0.5"
             style={{ color: "var(--gp-text-muted)" }}>
            {rule.trigger_label} → {rule.action_label}
          </p>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
              style={{
                background: rule.enabled ? "rgba(16,185,129,0.10)" : "rgba(148,163,184,0.10)",
                color:      rule.enabled ? "#047857" : "#64748B",
              }}>
          {rule.enabled ? "Active" : "Disabled"}
        </span>
      </div>
      {rule.description ? (
        <p className="text-[11.5px] leading-snug mt-1"
           style={{ color: "var(--gp-text-muted)" }}>{rule.description}</p>
      ) : null}

      {editing ? (
        <div className="mt-3 pt-3 space-y-3"
             style={{ borderTop: "1px solid var(--gp-card-border)" }}>
          <PremiumInput
            label="Threshold"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            type="number"
            min={1}
            testid={`auto-rule-threshold-${rule.id}`}
          />
          <label className="inline-flex items-center gap-2 text-[12px]"
                 style={{ color: "var(--gp-text)" }}>
            <input type="checkbox" checked={approval}
                   onChange={(e) => setApproval(e.target.checked)}
                   data-testid={`auto-rule-approval-${rule.id}`} />
            Approval required before execution
          </label>
          <div className="flex items-center gap-2">
            <button type="button" onClick={save} disabled={busy}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5"
              style={{ background: "rgba(16,185,129,0.10)", color: "#047857",
                       border: "1px solid rgba(16,185,129,0.22)" }}
              data-testid={`auto-rule-save-${rule.id}`}>
              <Save size={11} /> Save
            </button>
            <button type="button" onClick={() => setEditing(false)} disabled={busy}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5"
              style={{ background: "rgba(148,163,184,0.08)", color: "#475569",
                       border: "1px solid rgba(148,163,184,0.20)" }}>
              <X size={11} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 pt-3 flex items-center justify-between gap-2 flex-wrap"
             style={{ borderTop: "1px solid var(--gp-card-border)" }}>
          <p className="text-[10.5px] tabular-nums"
             style={{ color: "var(--gp-text-subtle)" }}>
            Last fired {rule.last_run_at ? fmtRelativeTime(rule.last_run_at) : "—"} · approval {rule.approval_required ? "required" : "auto"}
          </p>
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => setEditing(true)}
              className="text-[10.5px] font-semibold px-2.5 py-1 rounded-md inline-flex items-center gap-1"
              style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA",
                       border: "1px solid rgba(99,102,241,0.22)" }}
              data-testid={`auto-rule-edit-${rule.id}`}>
              <Pencil size={10} /> Edit
            </button>
            <button type="button" onClick={toggleEnabled} disabled={busy}
              className="text-[10.5px] font-semibold px-2.5 py-1 rounded-md inline-flex items-center gap-1"
              style={{ background: "rgba(148,163,184,0.08)", color: "#475569",
                       border: "1px solid rgba(148,163,184,0.20)" }}
              data-testid={`auto-rule-toggle-${rule.id}`}>
              <Power size={10} /> {rule.enabled ? "Disable" : "Enable"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const RuleCatalogue = ({ rules, refetch }) => (
  <section className="mb-7" data-testid="auto-catalogue">
    <div className="flex items-center gap-2 mb-3">
      <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: "var(--gp-violet)" }} />
      <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">
        Rule Catalogue
      </p>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rules.map((r) => <RuleCatalogueCard key={r.id} rule={r} refetch={refetch} />)}
    </div>
  </section>
);

/* ─── 6. Governance Footer ──────────────────────────────────────────── */
const GovernanceFooter = () => (
  <section className="gp-card p-5 sm:p-6 mt-2" data-testid="auto-governance-footer">
    <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700 mb-2">
      Operational Governance
    </p>
    <p className="text-[12.5px] sm:text-[13px] font-semibold leading-snug mb-3"
       style={{ color: "var(--gp-text)" }}>
      ChainFlow augments operational coordination · it does not replace leadership judgement.
    </p>
    <ul className="space-y-1.5 text-[11.5px] leading-snug"
        style={{ color: "var(--gp-text-muted)" }}>
      <li>· Every automation is approval-gated by default · no silent autonomous execution.</li>
      <li>· Every fired, approved, or rejected run writes an immutable DealActivity audit row.</li>
      <li>· Every read and write is org-scoped and CSRF-protected.</li>
      <li>· Director oversight is preserved at every step of the operational pipeline.</li>
    </ul>
  </section>
);

/* ─── Page shell ────────────────────────────────────────────────────── */
export default function AutomationsPage({ embedded = false }) {
  const { summary, runs, rules, recent, loading, error, refetch } = useLiveAutomations({
    status: "pending_approval",
  });
  if (loading) {
    return (
      <DirectorShell testid="automations-page" embedded={embedded}>
        <div className="px-5 sm:px-7 lg:px-9 pt-6 pb-12">
          <div className="gp-card p-7" style={{ color: "var(--gp-text-muted)" }}>
            Composing Intervention Command Desk…
          </div>
        </div>
      </DirectorShell>
    );
  }
  if (error) {
    return (
      <DirectorShell testid="automations-page" embedded={embedded}>
        <div className="px-5 sm:px-7 lg:px-9 pt-6 pb-12">
          <div className="gp-card p-7"
               style={{ color: "var(--gp-text)", borderLeft: "3px solid #F59E0B" }}>
            <p className="text-[13px] font-semibold mb-1">Access required</p>
            <p className="text-[12px]" style={{ color: "var(--gp-text-muted)" }}>{error}</p>
          </div>
        </div>
      </DirectorShell>
    );
  }
  return (
    <DirectorShell testid="automations-page" embedded={embedded}>
      <div className={embedded ? "" : "px-5 sm:px-7 lg:px-9 pt-6 pb-12"}>
        <AutomationHero summary={summary} />
        <PendingApprovalsQueue runs={runs} rules={rules} refetch={refetch} />
        <RuleActivityHealth summary={summary} />
        <RecentAutomationTimeline recent={recent} rules={rules} />
        <RuleCatalogue rules={rules} refetch={refetch} />
        <GovernanceFooter />
      </div>
    </DirectorShell>
  );
}
